import ERROR_TYPES from '@/constants/errorTypes'
import { hostOf } from '@/utilities/url'
import type { LogsEvent } from '@datadog/browser-logs'

/**
 * Datadog ブラウザログのレベル判定ポリシー
 *
 * ブラウザログの大半は広告・計測ドメインへの network エラーとクローラ由来で、status も error に偏っていた（精査結果は #8882）。
 * 「送らない / warn に下げる / error のまま」をここで一箇所で決める。
 * SDK 自動収集分（network / source / console）は createBeforeSend が、アプリの明示送信は
 * levelForApiError / levelForErrorCause を呼び出し側で使う。
 */

export type LogLevel = 'debug' | 'info' | 'warn' | 'error'

/** モニター「Vueテンプレートファイルの読み込みエラー」(178196775) が数える error_type。値を変えるとモニターが沈黙する */
const DYNAMIC_IMPORT_FAILURE_TYPE = 'dynamic_import_failure'

/**
 * クローラ・ヘッドレスブラウザ・計測ツール。Googlebot/2.1・AdsBot-Google・bingbot/2.0・PetalBot;・Bytespider・HeadlessChrome・
 * Chrome-Lighthouse（PageSpeed）・Google-InspectionTool・facebookexternalhit を想定。
 * `bot` の直後を記号に限定しているのは、CUBOT 端末の実ブラウザ UA（"CUBOT KINGKONG"）を巻き込まないため
 */
const BOT_USER_AGENT_PATTERN = /bot[/\-;)]|spider|crawler|headlesschrome|lighthouse|inspectiontool|facebookexternalhit/i

/** デプロイ直後に旧ページが新ハッシュの chunk を取りに行って失敗するケース（Chrome / Safari / Firefox / Vite の文言）。障害ではない */
const CHUNK_LOAD_ERROR_PATTERN = /Failed to fetch dynamically imported module|Unable to preload CSS|Importing a module script failed|error loading dynamically imported module/i

/** クロスオリジンスクリプトのエラーはブラウザが詳細を隠すため、原因調査ができない */
const OPAQUE_SCRIPT_ERROR_PATTERN = /^(Uncaught\s+"?)?Script error\.?"?$/

/** console.error 経由で上がる通信失敗。survey.js 等の第三者スクリプトの fetch 失敗が大半 */
const CONSOLE_NETWORK_FAILURE_PATTERN = /Failed to fetch|Load failed|NetworkError/

export function isBotUserAgent(userAgent: string): boolean {
  return BOT_USER_AGENT_PATTERN.test(userAgent)
}

/**
 * axios のエラーからレベルを決める
 * - サーバが応答した: 5xx は error、4xx（未ログインの 403 等）は warn
 * - 応答が無い（Network Error / timeout / abort）: ユーザー側の通信事情が大半なので warn
 * - リクエストを組み立てる前に失敗: コードの不具合なので error
 */
export function levelForApiError(error: { response?: { status: number }; request?: unknown }): LogLevel {
  if (error.response) return error.response.status >= 500 ? 'error' : 'warn'
  if (error.request) return 'warn'
  return 'error'
}

/** errorStore の cause（ERROR_TYPES）からレベルを決める。入力不備は利用者側の事象なので info */
export function levelForErrorCause(cause: string): LogLevel {
  switch (cause) {
    case ERROR_TYPES.VALIDATION_ERROR:
      return 'info'
    case ERROR_TYPES.GLOBAL_ERROR:
      return 'warn'
    default:
      return 'error'
  }
}

/** stack の 1 行目は "Name: message" なので、message に含まれる URL を拾わないようフレーム行だけを見る */
const firstUrlInStackFrames = (stack: string | undefined): string | undefined =>
  stack?.split('\n').slice(1).join('\n').match(/https?:\/\/[^\s)]+/)?.[0]

const markAsChunkLoadFailure = (event: LogsEvent): void => {
  event.status = 'warn'
  event.error_type = DYNAMIC_IMPORT_FAILURE_TYPE
}

/**
 * SDK の beforeSend を生成する
 * @param firstPartyHosts 自ドメインとみなすホスト（ページ・API・romanee・バンドル配信元）。host はポート込み
 * @returns false を返したイベントは送信されない。status / error_type は書き換えて送る
 */
export function createBeforeSend(firstPartyHosts: string[]): (event: LogsEvent) => boolean {
  const isFirstParty = (host: string | null): boolean => host !== null && firstPartyHosts.includes(host)

  return (event: LogsEvent): boolean => {
    switch (event.origin) {
      case 'network': {
        // 第三者ドメイン（広告・計測・Datadog intake）と URL 不正は送らない
        if (!isFirstParty(hostOf(event.http?.url))) return false
        // 自ドメインの切断・タイムアウト。5xx は error のまま
        if (event.http?.status_code === 0) event.status = 'warn'
        return true
      }
      case 'source': {
        if (OPAQUE_SCRIPT_ERROR_PATTERN.test(event.message)) return false
        if (CHUNK_LOAD_ERROR_PATTERN.test(event.message)) {
          markAsChunkLoadFailure(event)
          return true
        }
        const stackHost = hostOf(firstUrlInStackFrames(event.error?.stack))
        // 第三者スクリプト（clarity.js / recaptcha / gtm.js）起因は自分たちでは直せない
        if (stackHost !== null && !isFirstParty(stackHost)) event.status = 'warn'
        return true
      }
      case 'console': {
        if (CONSOLE_NETWORK_FAILURE_PATTERN.test(event.message)) event.status = 'warn'
        return true
      }
      case 'logger': {
        // Vue errorHandler が捕まえた chunk 読み込み失敗もここで揃える
        if (CHUNK_LOAD_ERROR_PATTERN.test(event.message)) markAsChunkLoadFailure(event)
        return true
      }
      default:
        return true
    }
  }
}
